import _ from 'lodash';
import React, { SyntheticEvent } from 'react';
import classnames from 'classnames';
import { isLayerType } from 'common/utils/is-layer-type';
import { BBCommonProps } from 'types/story';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import css from 'client/components/common/BuildingBlocks/Fields/TextField/TextField.scss';
import { COMPONENT_STATES, UNIFORM_PROPS } from 'common/constants';
import {
	FIELD_BB_TYPE,
	FIELD_DATA_ATTR,
	FIELD_EDIT_MODE,
	FIELD_EDITABLE_ATTR,
} from 'client/components/common/BuildingBlocks/Fields/constants';
import { Error } from '../Error';
import { withField, placeholderColorPath, placeholderPath, placeholderShowPath } from '../utils';
import { TFieldProps, TFieldState } from '../types';

type State = TFieldState;

type Props = BBCommonProps & {
	getErrorMsg: (...args: any[]) => any;
};

class TextField extends React.Component<Props, State> {
	state = {
		editMode: '',
		value: _.get(this.props, placeholderPath),
		readOnly: true,
		pending: false, // pending for a value storing
	};

	static getDerivedStateFromProps(props: Props, state: State) {
		const nextState: Partial<State> = {
			editMode: _.get(props, placeholderShowPath) ? FIELD_EDIT_MODE.PLACEHOLDER : FIELD_EDIT_MODE.VALUE,
		};

		const placeholder: string | undefined = _.get(props, placeholderPath);
		if (placeholder === state.value && state.pending) {
			nextState.value = placeholder;
			nextState.readOnly = true;
			nextState.pending = false;
		}

		// in case of value is updated when edited master symbols from other instance
		if (state.readOnly && placeholder !== state.value) {
			nextState.value = placeholder;
		}

		return nextState;
	}

	get path() {
		return _.get(this.props.editableModeProps, ['nodeProps', 'data-path']);
	}

	getFieldProps() {
		const type = _.get(this.props, ['uiConfig', 'componentProps', UNIFORM_PROPS.fieldType], FIELD_BB_TYPE.TEXT);
		const { name } = this.props.uiConfig.editorProps;
		const placeholder = _.get(this.props, ['uiConfig', 'componentProps', UNIFORM_PROPS.fieldPlaceholder]);
		const color = _.get(this.props, placeholderColorPath);

		const props: TFieldProps = {
			className: css.field,
			type,
			name,
			placeholder,
			autoComplete: 'off',
			[FIELD_DATA_ATTR]: '',
		};

		if (this.props.isEditableMode) {
			props.value = this.state.readOnly ? props.placeholder : this.state.value;
			delete props.placeholder;
			props.readOnly = this.state.readOnly;
			props.onDoubleClick = e => {
				this.setState({ readOnly: false });
			};
			props.onChange = e => {
				this.setState({ value: e.target.value });
			};
			props.onBlur = this.onEditableBlur;

			if (this.state.editMode === FIELD_EDIT_MODE.PLACEHOLDER) {
				_.set(props, `style.color`, color);
			} else {
				_.set(props, `style.color`, '');
			}

			if (!props.readOnly) {
				props[FIELD_EDITABLE_ATTR] = ''; // needed to control component drag
			}

			props.onFocus = (event: SyntheticEvent) => {
				// prevent this block from being selected when clicking on its parent
				event.stopPropagation();
			};
		}

		return props;
	}

	onEditableBlur = () => {
		if (this.state.value === _.get(this.props, placeholderPath)) {
			this.setState({ readOnly: true });
			return;
		}

		transmitToAdminBbUpdate({
			id: TextField.name,
			path: `elements.${this.path}.${placeholderPath}`,
			value: this.state.value,
			isStory: isLayerType(this.props).global,
		});

		this.setState({ pending: true });
	};

	renderEditable() {
		const props = { ...this.props.editableModeProps?.nodeProps, className: css.editable };
		return this.renderDefault(props);
	}

	renderDefault(props: any = {}) {
		const { nodeProps, editorProps } = this.props.uiConfig;
		const className = classnames(css.textField, nodeProps.className, props.className);
		const isIncorrect = _.has(props, 'data-incorrect-state') || this.props.states[COMPONENT_STATES.INCORRECT];
		const errorMessage = this.props.getErrorMsg(props);

		const common = {
			...this.props.uiConfig.nodeProps,
			...this.props.stateAttrs,
			...this.props.eventListeners,
		};

		return (
			<div {...common} {...props} className={className}>
				<input {...this.getFieldProps()} />
				<Error
					isHidden={!isIncorrect || typeof errorMessage === 'undefined'}
					path={this.path}
					isEditableMode={!!this.props.isEditableMode}
					errorType={editorProps[UNIFORM_PROPS.fieldErrorShow]}
					text={errorMessage}
				/>
			</div>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

export default withCardTransitionContext(withField(TextField));
